import { jsx as _jsx } from "react/jsx-runtime";
import qs from 'qs';
import React, { createContext, useContext, useMemo, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
// Reducer for managing filter state
const reducer = (state, action) => {
    switch (action.type) {
        case 'setFilters': {
            return {
                ...state,
                ...action.payload,
            };
        }
        case 'setQuery': {
            return {
                ...state,
                offset: 0, // reset offset when query changes
                query: action.payload,
            };
        }
        case 'setLimit': {
            return {
                ...state,
                limit: action.payload,
            };
        }
        case 'setOffset': {
            return {
                ...state,
                offset: action.payload,
            };
        }
        case 'reset': {
            return action.payload;
        }
        default: {
            return state;
        }
    }
};
// Parse query string utility
const parseQueryString = (queryString, additionals = null, allowedFilters = [], defaultLimit = 15) => {
    const defaultVal = {
        offset: 0,
        limit: defaultLimit,
        additionalFilters: additionals,
    };
    if (queryString) {
        const filters = qs.parse(queryString);
        for (const [key, value] of Object.entries(filters)) {
            if (allowedFilters.includes(key)) {
                switch (key) {
                    case 'offset': {
                        if (typeof value === 'string') {
                            defaultVal.offset = parseInt(value);
                        }
                        break;
                    }
                    case 'limit': {
                        if (typeof value === 'string') {
                            defaultVal.limit = parseInt(value);
                        }
                        break;
                    }
                    case 'q': {
                        if (typeof value === 'string') {
                            defaultVal.query = value;
                        }
                        break;
                    }
                    default: {
                        defaultVal[key] = value;
                        break;
                    }
                }
            }
        }
    }
    return defaultVal;
};
// Default query properties
const defaultQueryProps = {
    offset: 0,
    limit: 15,
    additionalFilters: null,
};
const FilterContext = createContext(undefined);
export const useFilterContext = () => {
    const context = useContext(FilterContext);
    if (!context) {
        throw new Error('useFilterContext must be used within a FilterProvider');
    }
    return context;
};
export const FilterProvider = ({ children, allowedFilters = ['offset', 'limit', 'q', 'created_at', 'began_processing_at', 'reviewed_at', 'converted_at'], defaultLimit = 15 }) => {
    const [selectedDateField, setSelectedDateField] = useState('created_at');
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    let location;
    try {
        location = useLocation();
    }
    catch (error) {
        console.warn('useLocation failed - Router context may be missing:', error);
        location = { search: '', pathname: '' };
    }
    const getSelectedDateFieldFromQueryString = (search) => {
        const filters = qs.parse(search);
        const parsedFilters = {};
        for (const entry of Object.entries(filters)) {
            let key = entry[0];
            const value = entry[1];
            if (key[0] == '?') {
                key = key.substring(1);
            }
            if (key in parsedFilters) {
                if (typeof value === 'object') {
                    parsedFilters[key] = { ...parsedFilters[key], ...value };
                }
            }
            else {
                parsedFilters[key] = value;
            }
        }
        for (const entry of Object.entries(parsedFilters)) {
            const key = entry[0];
            if (key === 'created_at' || key === 'began_processing_at' || key === 'reviewed_at' || key === 'converted_at') {
                return key;
            }
        }
        return 'created_at';
    };
    const parseQueryStringAndSet = (search) => {
        const initial = parseQueryString(search, defaultQueryProps.additionalFilters, allowedFilters, defaultLimit);
        return initial;
    };
    const initial = useMemo(() => {
        return parseQueryStringAndSet(location.search);
    }, [location.search, allowedFilters, defaultLimit]);
    // Handle selectedDateField separately to avoid infinite re-renders
    React.useEffect(() => {
        const selectedDateField = getSelectedDateFieldFromQueryString(location.search);
        setSelectedDateField(selectedDateField);
    }, [location.search]);
    const [state, dispatch] = useReducer(reducer, initial);
    const setSingleFilter = (filter, keepPagination = false) => {
        const nextState = { ...state, ...filter };
        dispatch({ type: 'setFilters', payload: nextState });
        if (!keepPagination)
            dispatch({ type: 'setOffset', payload: 0 });
    };
    return (_jsx(FilterContext.Provider, { value: {
            isLoadingOrders,
            setIsLoadingOrders,
            state,
            dispatch,
            setSingleFilter,
            selectedDateField,
            setSelectedDateField,
        }, children: children }));
};
//# sourceMappingURL=filter-provider.js.map